import { ellipsisString, uuid } from "@/app/infrastructures/misc/Utils";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import jsPDF from "jspdf";
import { logoBlack } from "../../constants/images";

const isBossPackOrSameDay = (data: any) => {
  const validTypes = ["BOSSPACK", "SAMEDAY", "VIPPACK"];
  return validTypes.includes(data);
};
const getPackAbbreviation = (fullName: string) => {
  const abbreviations: any = {
    BOSSPACK: "BOSS",
    REGPACK: "REG",
    JAGOPACK: "JAGO",
    ONEPACK: "ONE",
    INTERPACK: "INTER",
    BIGPACK: "BIG",
    JUMBOPACK: "JUMBO",
    SAMEDAY: "SAME",
    OTOPACK150: "OTO15",
    OTOPACK250: "OTO25",
    VIPPACK: "VIP",
    INTERPACKMAXI: "MAXI",
    BIGPACKFAST: "BIGF",
    MINIPACK: "MINI"
  };

  return abbreviations[fullName] || fullName; // Return the abbreviation or the original fullName if not found
};

interface BadgeConfig {
  doc: jsPDF;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

const renderBadge = (config: BadgeConfig) => {
  config.doc.setLineWidth(0.3);
  config.doc.setFillColor("#333333");
  config.doc.setDrawColor("#333333");
  config.doc.roundedRect(config.x, config.y, config.width, config.height, 0.5, 0.5, "FD");
  config.doc.setFont("Montserrat", "bold");
  config.doc.setTextColor("#FFFFFF");
  config.doc.setFontSize(15);
  config.doc.text(config.text, config.x + 1, config.y + 6);
};

const renderProductType = (params: any, isVIPPack: boolean, abbreviationX: number) => {
  if (isBossPackOrSameDay(params.data.sttProductType)) {
    const isInterMaxiType = params.data.sttProductType === "INTERPACKMAXI";

    const roundWidth = isVIPPack ? 12 : isInterMaxiType ? 18.5 : 19;
    const startXMargin = isVIPPack ? 64 : isInterMaxiType ? 57.5 : 57;
    params.doc.roundedRect(
      params.startX + startXMargin,
      params.startY + 3,
      roundWidth,
      8,
      0.5,
      0.5,
      "FD"
    );
  }
  
  params.doc.setFont("Montserrat", "bold");
  params.doc.setTextColor(
    isBossPackOrSameDay(params.data.sttProductType) ? "#FFFFFF" : "#333333"
  );
  params.doc.setFontSize(15);
  params.doc.text(
    getPackAbbreviation(params.data.sttProductType),
    abbreviationX,
    params.startY + 9,
    { align: "right" }
  );
};

const renderStatusBadges = (params: any, abbreviationX: number, abbreviationWidth: number) => {
  if (params.data.isDangerousGoods) {
    renderBadge({
      doc: params.doc,
      text: "DG",
      x: abbreviationX - abbreviationWidth - 12.5,
      y: params.startY + 3,
      width: 10,
      height: 8
    });
  }

  if (params.data.sttCommodityIsQuarantine) {
    const xOffset = params.data.isDangerousGoods ? 19 : 8.5;
    renderBadge({
      doc: params.doc,
      text: "K",
      x: abbreviationX - abbreviationWidth - xOffset,
      y: params.startY + 3,
      width: 6,
      height: 8
    });
  }
};

const renderLogo = (params: any, logoAspectRatio: number) => {
  params.doc.addImage(
    logoBlack,
    "PNG",
    params.startX + 5,
    params.startY + 3,
    32,
    logoAspectRatio * 32,
    "logo",
    "FAST",
    0
  );
};

const renderBarcode = (params: any) => {
  params.doc.addImage(
    params.barcode,
    "PNG",
    params.startX + 5,
    params.startY + 19,
    69,
    15,
    uuid(),
    "FAST",
    0
  );
  params.doc.setFont("Montserrat", "bold");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(18);
  params.doc.text(params.barcodeValue, params.startX + 5, params.startY + 19);
};

const renderReference = (params: any) => {
  params.doc.setFont("Montserrat", "normal");
  params.doc.setFontSize(6);
  params.doc.text("Ref: ", params.startX + 5, params.startY + 35);
  params.doc.setFont("Montserrat", "bold");
  params.doc.text(
    params.data.sttNoRefExternal ? ellipsisString(params.data.sttNoRefExternal, 35) : "-",
    params.startX + 10,
    params.startY + 35
  );
};

const renderClientName = (params: any) => {
  const elipsisClientName =
    params.data.sttClientName.length > 40
      ? `${params.data.sttClientName.substring(0, 40)}...`
      : params.data.sttClientName;
  params.doc.text(elipsisClientName, params.startX + 5, params.startY + 38);
};

const renderLines = (params: any) => {
  params.doc.setLineWidth(0.4);
  params.doc.line(params.startX, params.startY + 40, params.startX + 105, params.startY + 40);
  params.doc.line(params.startX + 77, params.startY + 40, params.startX + 77, params.startY);
};

const renderShipmentId = (params: any) => {
  params.doc.setFont("Montserrat", "bold");
  params.doc.setTextColor("#333333");
  params.doc.setFontSize(6);
  params.doc.text(params.data.bookingId || params.shipmentId, params.startX + 72, params.startY + 35, "right");
};

const calculateAbbreviationX = (params: any, isVIPPack: boolean) => {
  const x = getPackAbbreviation(params.data.sttProductType).length > 4
    ? params.startX + 76
    : params.startX + 74;
  return isVIPPack ? x + 1 : x;
};

export const headerSection = async (
  params: {
    doc: jsPDF | any;
    bookingType: string;
    accountType: string;
    data: PrintDataEntitas;
    pieceData: any;
    barcode: string;
    vendorRefCode: string;
    barcodeValue: string;
    vendorRefCodeValue: string;
    logo: string;
    shipmentId: string;
    startX: number;
    startY: number;
  },
  logoAspectRatio: number
) => {
  renderLogo(params, logoAspectRatio);

  const isVIPPack = params.data.sttProductType === "VIPPACK";
  const abbreviationX = calculateAbbreviationX(params, isVIPPack);

  params.doc.setLineWidth(0.3);
  params.doc.setFillColor("#333333");
  params.doc.setDrawColor("#333333");
  
  renderProductType(params, isVIPPack, abbreviationX);

  const abbreviationWidth = params.doc.getTextDimensions(
    getPackAbbreviation(params.data.sttProductType)
  ).w;
  renderStatusBadges(params, abbreviationX, abbreviationWidth);

  renderBarcode(params);
  renderReference(params);
  renderClientName(params);
  renderLines(params);
  renderShipmentId(params);

  return {
    ...params,
    startX: 77,
    startY: 40
  };
};